"use client";

import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import {
  ArrowUpRight,
  Clock,
  ListTodo,
  Sparkles,
  Volume2,
  Speech,
  Languages,
  CheckCircle2,
  Target,
  TrendingUp,
  Hash,} from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/interactive-empty-state";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import AudioWave from "@/components/ui/audio-wave";
import { SpeakerIcon } from "@/components/SpeakerIcon";
import { SpinnerIcon } from "@/components/ui/SpinnerIcon";
import { useAudioRecorder } from "@/hooks/useAudioRecorder";
import { useSaveShadowingRecord } from "@/hooks/useShadowingStats";
import { useUser } from "@/lib/context/user-context";
import { useI18n } from "@/lib/i18n";
import { calculateOptimalSimilarity } from "@/lib/text-similarity";
import { useEpisodeProgress } from "@/hooks/useEpisodeProgress";
import { TextDiffHighlight } from "@/lib/text-diff-highlight-improved";
import { toast } from "sonner";
import { IconPlayerPause, IconPlayerPlay } from "@tabler/icons-react";
import { CardStack } from "@/components/cardStack/CardStack";
import type { StackableItem } from "@/lib/types";

export type QuizItem = {
  sequence?: number;
  term?: string;
  explanation?: string | Record<string, string>;
  english?: string;
  chinese?: string;
  originalAudioPath?: string;
  audioUrl?: string;
  timestamp?: number;
  endTimestamp?: number;
};

// 辅助函数：从 string 或 Record<string, string> 中提取字符串
function extractStringValue(value: string | Record<string, string> | undefined, preferredLang?: string): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    // 优先使用指定语言，然后 zh，最后取第一个值
    const lang = preferredLang || 'zh';
    return value[lang] || value['zh'] || value['ja'] || value['en'] || Object.values(value)[0] || '';
  }
  return '';
}

type QuizStackItem = StackableItem & {
  item: QuizItem;
  colorIndex: number;
};

interface QuizSectionProps {
  quizItems?: QuizItem[] | null;
  isLoading?: boolean;
  onTimestampClick?: (timestamp: number, endTimestamp?: number) => void;
  episodeId?: string;
  episodeTitle?: string;
  episodeThumbnail?: string;
  onQuizItemsUpdate?: (items: QuizItem[]) => void;
  onGenerateQuiz?: () => Promise<void>;
  hasVocabulary?: boolean; // 是否有 vocabulary 数据，只有有 vocabulary 时才显示 Generate Quiz
}

export const QuizSection: React.FC<QuizSectionProps> = ({
  quizItems = [],
  isLoading = false,
  onTimestampClick,
  episodeId,
  episodeTitle,
  episodeThumbnail,
  onQuizItemsUpdate,
  onGenerateQuiz,
  hasVocabulary = false,
}) => {
  const [items, setItems] = useState<QuizItem[]>(Array.isArray(quizItems) ? quizItems : []);
  const [isGeneratingQuiz, setIsGeneratingQuiz] = useState(false);
  const { user } = useUser();
  const { t } = useI18n();

  // 获取 episode quiz 进度
  const { data: progressData, loading: progressLoading, isQuizCompleted, refresh: refreshProgress } = useEpisodeProgress(user?.id, episodeId);

  useEffect(() => {
    setItems(Array.isArray(quizItems) ? quizItems : []);
  }, [quizItems]);

  // 监听进度更新事件，立即刷新UI
  useEffect(() => {
    const handleProgressUpdate = (event: CustomEvent) => {
      const detail = event.detail;
      if (detail?.episodeId?.includes(':quiz')) {
        const [savedEpisodeId] = detail.episodeId.split(':');
        if (savedEpisodeId === episodeId) {
          // 延迟刷新，确保后端数据已更新
          setTimeout(() => {
            refreshProgress();
          }, 1000);
        }
      }
    };

    window.addEventListener('shadowingRecordSaved', handleProgressUpdate as EventListener);
    return () => {
      window.removeEventListener('shadowingRecordSaved', handleProgressUpdate as EventListener);
    };
  }, [episodeId, refreshProgress]);

  const sortedItems = useMemo(() => {
    if (!Array.isArray(items)) {
      return [];
    }

    return [...items].sort((a, b) => {
      const aSeq = a.sequence ?? 0;
      const bSeq = b.sequence ?? 0;
      if (aSeq === bSeq) {
        const aStart = a.timestamp ?? 0;
        const bStart = b.timestamp ?? 0;
        return aStart - bStart;
      }
      return aSeq - bSeq;
    });
  }, [items]);

  const handleAudioGenerated = useCallback(
    (sequence: number | undefined, english: string | undefined, audioUrl: string) => {
      setItems((prev) => {
        if (!Array.isArray(prev) || prev.length === 0) return prev;

        let changed = false;
        const updated = prev.map((quiz) => {
          const sequenceMatch = sequence !== undefined && quiz.sequence === sequence;
          const englishMatch = !sequenceMatch && english && quiz.english === english;
          if (sequenceMatch || englishMatch) {
            changed = true;
            return { ...quiz, audioUrl };
          }
          return quiz;
        });

        if (changed) {
          // 不再调用 onQuizItemsUpdate，因为我们已经通过专门的 API 保存了 audioUrl
          // onQuizItemsUpdate?.(updated);
          return updated;
        }

        return prev;
      });
    },
    [] // 移除 onQuizItemsUpdate 依赖
  );

  // Calculate completion progress
  const completionStats = useMemo(() => {
    const completedCount = sortedItems.filter((item) =>
      isQuizCompleted(item.sequence || 0)
    ).length;
    const totalCount = sortedItems.length;
    const completionPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

    return {
      completedCount,
      totalCount,
      completionPercentage
    };
  }, [sortedItems, isQuizCompleted]);

  // Calculate average accuracy from real shadowing records
  const averageAccuracy = useMemo(() => {
    if (!progressData?.progress?.quizzes || !user?.id || !episodeId) return null;

    // Calculate average accuracy from real shadowing records
    const quizAccuracies: number[] = [];

    Object.entries(progressData.progress.quizzes).forEach(([sequence, isCompleted]) => {
      if (isCompleted) {
        // Use real accuracy data from shadowing records if available
        const sequenceNum = Number(sequence);
        const accuracy = progressData.progress?.accuracy?.[sequenceNum];

        if (accuracy !== undefined && accuracy > 0) {
          quizAccuracies.push(accuracy);
        }
      }
    });

    if (quizAccuracies.length === 0) return null;

    return Math.round(quizAccuracies.reduce((sum, acc) => sum + acc, 0) / quizAccuracies.length);
  }, [progressData?.progress?.quizzes, progressData?.progress?.accuracy, user?.id, episodeId]);

  const questionLabel = sortedItems.length === 1
    ? t('quizSection.stats.questionSingular', 'question')
    : t('quizSection.stats.questionPlural', 'questions');
  const overviewCountLabel = sortedItems.length === 1
    ? t('quizSection.stats.countSingular', 'Question')
    : t('quizSection.stats.countPlural', 'Questions');
  const nextStudyItem = useMemo(() => {
    return sortedItems.find((quiz) => !isQuizCompleted(quiz.sequence ?? 0));
  }, [sortedItems, isQuizCompleted]);
  const nextTermDisplay = nextStudyItem
    ? (nextStudyItem.term || nextStudyItem.english || '—')
    : (sortedItems.length > 0
      ? t('quizSection.overview.allCompleted', 'All quiz items completed')
      : '—');

  const quizStackItems = useMemo<QuizStackItem[]>(
    () =>
      sortedItems.map((item, index) => ({
        id: `${item.sequence ?? index}-${item.term ?? item.english ?? 'quiz'}`,
        item,
        colorIndex: index,
      })),
    [sortedItems]
  );

  // 生成 Quiz 的处理函数
  const handleGenerateQuiz = useCallback(async () => {
    if (!episodeId) {
      toast.error(t('quizSection.toast.noEpisodeId', 'Episode ID is required to generate quiz'));
      return;
    }

    setIsGeneratingQuiz(true);
    try {
      // 如果有外部的生成函数，优先使用
      if (onGenerateQuiz) {
        await onGenerateQuiz();
      } else {
        // 否则调用默认的 API
        const response = await fetch('/api/admin/migrate-share-quiz', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            shareIds: [episodeId],
            forceUpdate: false,
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Failed to generate quiz');
        }

        // 检查是否成功生成了 quiz
        const successResult = result.results?.find((r: any) => r.success);
        if (successResult) {
          toast.success(t('quizSection.toast.quizGenerated', 'Quiz generated successfully'));
          
          // 🎯 从 Redis 重新获取最新的 quiz 数据
          try {
            const fetchResponse = await fetch(`/api/episodes/${episodeId}`);
            if (fetchResponse.ok) {
              const episodeData = await fetchResponse.json();
              const newQuizItems = episodeData?.content?.content?.vocabulary?.quiz || 
                                   episodeData?.content?.vocabulary?.quiz ||
                                   [];
              if (Array.isArray(newQuizItems) && newQuizItems.length > 0) {
                setItems(newQuizItems);
                onQuizItemsUpdate?.(newQuizItems);
              }
            }
          } catch (fetchError) {
            console.error('Failed to fetch updated quiz data:', fetchError);
            // 如果获取失败，刷新页面作为备选方案
            window.location.reload();
          }
        } else {
          const errorMessage = result.results?.[0]?.message || 'No quiz items generated';
          throw new Error(errorMessage);
        }
      }
    } catch (error) {
      console.error('Failed to generate quiz:', error);
      toast.error(error instanceof Error ? error.message : t('quizSection.toast.generateFailed', 'Failed to generate quiz'));
    } finally {
      setIsGeneratingQuiz(false);
    }
  }, [episodeId, onGenerateQuiz, onQuizItemsUpdate, t]);

  return (
    <div className="flex flex-col w-full rounded-3xl border border-border/50 bg-background/30 backdrop-blur-sm overflow-hidden">
      <div className="mb-6 flex justify-between gap-3 border-b border-border/50 p-2 sm:p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-3">
          <div className="h-6 w-2 rounded-full bg-primary" />
          <div>
            <h3 className="text-xl font-bold text-foreground">{t('quizSection.title', 'Interactive Quiz')}</h3>
            <p className="text-sm text-muted-foreground">{t('quizSection.description', 'Practice key terms pulled from this episode.')}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            <ListTodo className="h-4 w-4" />
            <span className="whitespace-nowrap">{sortedItems.length} {questionLabel}</span>
          </span>
          {/* {completionStats.completedCount > 0 && (
            <span className="flex items-center gap-1 text-xs uppercase tracking-wide text-emerald-600/80">
              <CheckCircle2 className="h-4 w-4" />
              {completionStats.completedCount}/{completionStats.totalCount} completed
            </span>
          )}
          {averageAccuracy !== null && (
            <span className="flex items-center gap-1 text-xs uppercase tracking-wide text-blue-600/80">
              <Sparkles className="h-4 w-4" />
              {averageAccuracy}% accuracy
            </span>
          )} */}
        </div>
      </div>

      <div className="flex-1 px-4 sm:px-6 pb-6 sm:pb-8">
        {isLoading ? (
          <QuizSectionSkeleton />
        ) : sortedItems.length === 0 ? (
          hasVocabulary ? (
            // 有 vocabulary 时显示 Generate Quiz 按钮
            <EmptyState
              title={isGeneratingQuiz 
                ? t('quizSection.empty.generating', 'Generating Quiz...')
                : t('quizSection.empty.generateTitle', 'Generate Quiz')}
              description={isGeneratingQuiz
                ? t('quizSection.empty.generatingDesc', 'Please wait while we create quiz questions from your vocabulary.')
                : t('quizSection.empty.generateDesc', 'Generate interactive quiz questions based on vocabulary from this episode.')}
              icons={[
                <ListTodo key="quiz" className="h-6 w-6" />,
                <Sparkles key="sparkles" className="h-6 w-6" />,
                <Volume2 key="audio" className="h-6 w-6" />,
              ]}
              action={{
                label: isGeneratingQuiz 
                  ? t('quizSection.empty.generatingBtn', 'Generating...')
                  : t('quizSection.empty.generateBtn', 'Generate Quiz'),
                icon: isGeneratingQuiz 
                  ? <SpinnerIcon size="sm" />
                  : <Sparkles className="h-4 w-4" />,
                onClick: handleGenerateQuiz,
                disabled: isGeneratingQuiz || !episodeId,
              }}
            />
          ) : (
            // 没有 vocabulary 时显示提示信息
            <EmptyState
              title={t('quizSection.empty.title', 'No quiz available yet')}
              description={t('quizSection.empty.description', 'Quiz questions will appear here once vocabulary has been generated.')}
              icons={[
                <ListTodo key="quiz" className="h-6 w-6" />,
                <Sparkles key="sparkles" className="h-6 w-6" />,
                <Volume2 key="audio" className="h-6 w-6" />,
              ]}
            />
          )
        ) : (
          <div className="grid gap-2 sm:gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
            <Card className="order-2 h-fit rounded-3xl border bg-muted/30 lg:order-1 lg:sticky lg:top-20">
              <CardContent className="space-y-2 sm:space-y-4 p-3 sm:p-5">
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t('quizSection.overview.title', 'Quiz Overview')}
                  </div>
                  <div className="mt-1 text-xl sm:text-2xl font-semibold">
                    {sortedItems.length} {overviewCountLabel}
                  </div>
                </div>

                {progressLoading ? (
                  <OverviewSkeleton />
                ) : (
                  <div className="space-y-3">
                    <OverviewRow
                      label={t('quizSection.overview.nextTerm', 'Next term')}
                      value={nextTermDisplay}
                      icon={<Hash className="h-4 w-4" />}
                    />
                    <OverviewRow
                      label={t('quizSection.overview.progress', 'Progress')}
                      value={`${completionStats.completedCount}/${completionStats.totalCount} (${completionStats.completionPercentage}%)`}
                      icon={<Target className="h-4 w-4" />}
                    />
                    <OverviewRow
                      label={t('quizSection.overview.accuracy', 'Accuracy')}
                      value={averageAccuracy !== null ? `${averageAccuracy}%` : "—"}
                      icon={<TrendingUp className="h-4 w-4" />}
                      highlight={averageAccuracy !== null}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="order-1 space-y-4 lg:order-2">
              {progressLoading ? (
                // Show skeleton cards while loading progress data
                sortedItems.map((item, index) => (
                  <QuizItemCardSkeleton
                    key={`skeleton-${item.sequence || 0}-${item.term || item.english || "term"}`}
                    colorIndex={index}
                  />
                ))
              ) : (
                <>
                  <div className="sm:hidden px-4">
                    <div className="flex justify-center">
                      <CardStack<QuizStackItem>
                        items={quizStackItems}
                        containerClassName="relative mx-auto h-[32rem] w-full max-w-[380px]"
                        cardClassName="absolute inset-0 h-full w-full cursor-grab"
                        stackConfig={{ rotation: 2, scale: 0.04, perspective: 900 }}
                      >
                        {(stackItem) => (
                          <QuizItemCard
                            item={stackItem.item}
                            onTimestampClick={onTimestampClick}
                            episodeId={episodeId}
                            episodeTitle={episodeTitle}
                            episodeThumbnail={episodeThumbnail}
                            onAudioGenerated={handleAudioGenerated}
                            colorIndex={stackItem.colorIndex}
                            isCompleted={isQuizCompleted(stackItem.item.sequence || 0)}
                            onProgressUpdate={refreshProgress}
                          />
                        )}
                      </CardStack>
                    </div>
                  </div>

                  <div className="hidden sm:flex sm:flex-col sm:space-y-4">
                    {sortedItems.map((item, index) => (
                      <QuizItemCard
                        key={`${item.sequence || 0}-${item.term || item.english || "term"}`}
                        item={item}
                        onTimestampClick={onTimestampClick}
                        episodeId={episodeId}
                        episodeTitle={episodeTitle}
                        episodeThumbnail={episodeThumbnail}
                        onAudioGenerated={handleAudioGenerated}
                        colorIndex={index}
                        isCompleted={isQuizCompleted(item.sequence || 0)}
                        onProgressUpdate={refreshProgress}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// 定义术语卡片背景颜色数组
const TERM_CARD_BACKGROUND_CLASSES = [
  "bg-[#0f172a]",
  "bg-[#fcd34d]",
  "bg-[#f472b6]",
  "bg-[#8b5cf6]",
  "bg-[#10b981]",
  "bg-[#eab308]",
  "bg-[#1e293b]",
  "bg-[#0f172a]",
];

function QuizItemCard({
  item,
  onTimestampClick,
  episodeId,
  episodeTitle,
  episodeThumbnail,
  onAudioGenerated,
  colorIndex,
  isCompleted,
  onProgressUpdate,
}: {
  item: QuizItem;
  onTimestampClick?: (timestamp: number, endTimestamp?: number) => void;
  episodeId?: string;
  episodeTitle?: string;
  episodeThumbnail?: string;
  onAudioGenerated?: (sequence: number | undefined, english: string | undefined, audioUrl: string) => void;
  colorIndex?: number;
  isCompleted?: boolean;
  onProgressUpdate?: () => void;
}) {
  const [showTranslation, setShowTranslation] = useState(false);
  const [currentAudioUrl, setCurrentAudioUrl] = useState<string | null>(item.audioUrl ?? null);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [ttsError, setTtsError] = useState<string | null>(null);
  const speakRef = useRef<HTMLDivElement>(null);
  const { t } = useI18n();

  useEffect(() => {
    setCurrentAudioUrl(item.audioUrl ?? null);
  }, [item.audioUrl]);

  useEffect(() => {
    if (currentAudioUrl) {
      setTtsError(null);
    }
  }, [currentAudioUrl]);

  const handleJump = () => {
    if (typeof item.timestamp === "number" && onTimestampClick) {
      onTimestampClick(
        item.timestamp,
        typeof item.endTimestamp === "number" ? item.endTimestamp : undefined
      );
    }
  };

  const handleGenerateAudio = useCallback(async () => {
    if (!item.english) {
      toast.error(t('quizSection.toast.noSentence', 'Quiz sentence unavailable for audio generation.'));
      return;
    }

    const defaultErrorMessage = t('quizSection.toast.audioGenerationFailed', 'Failed to generate audio');

    try {
      setTtsLoading(true);
      setTtsError(null);

      const response = await fetch("/api/quiz/generate-audio", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: item.english,
          episodeId,
          sequence: item.sequence,
          term: item.term,
        }),
      });

      const payload = await response.json().catch(() => null);

      if (!response.ok || !payload?.url) {
        const message = payload?.error || defaultErrorMessage;
        throw new Error(message);
      }

      setCurrentAudioUrl(payload.url);
      onAudioGenerated?.(item.sequence, item.english, payload.url);

      // Save the audio URL to Redis
      const updateResponse = await fetch("/api/quiz/update-audio", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          episodeId,
          sequence: item.sequence,
          audioUrl: payload.url,
        }),
      });

      if (!updateResponse.ok) {
        console.error("Failed to persist audio URL to Redis");
      }

      toast.success(t('quizSection.toast.audioGenerated', 'Quiz audio generated'));
    } catch (error) {
      console.error("Quiz audio generation failed", error);
      const message = error instanceof Error ? error.message : defaultErrorMessage;
      setTtsError(message);
      toast.error(message || defaultErrorMessage);
    } finally {
      setTtsLoading(false);
    }
  }, [episodeId, item.english, item.sequence, item.term, onAudioGenerated, t]);

  const durationLabel = useMemo(() => {
    let duration = 0;

    // If we have both timestamp and endTimestamp, use actual duration
    if (typeof item.timestamp === "number" && typeof item.endTimestamp === "number") {
      duration = Math.max(0, item.endTimestamp - item.timestamp);
    } else {
      // Otherwise, estimate duration based on sentence length
      const text = item.english || item.term || "";
      const wordCount = text.split(/\s+/).filter(word => word.length > 0).length;
      const estimatedDuration = Math.max(2, Math.min(8, wordCount / 2.0 + 1));
      duration = estimatedDuration;
    }

    return formatDuration(duration);
  }, [item.endTimestamp, item.timestamp, item.english, item.term]);

  const handleWordClick = useCallback(async (word: string) => {
    const trimmed = word.trim();
    if (!trimmed) return;

    try {
      const { doSpeak } = await import("@/components/tts");
      const controller = new AbortController();

      await doSpeak({
        provider: "EdgeTTS",
        text: trimmed,
        lang: "en",
        rate: 10,
        volume: 100,
        signal: controller.signal,
      });
    } catch (error) {
      console.error("Failed to speak word", error);
    }
  }, []);

  const interactiveSentence = useMemo<React.ReactNode[]>(() => {
    if (!item.english) return [];

    const termLower = item.term?.toLowerCase();
    const highlightIndex = (colorIndex ?? 0) % TERM_CARD_BACKGROUND_CLASSES.length;
    const termHighlightClass = TERM_CARD_BACKGROUND_CLASSES[highlightIndex];

    if (!termLower) {
      // If no term to highlight, just make words clickable
      const tokens = item.english.split(/(\s+|[^\w\s])/);
      return tokens.map((token, index) => {
        const trimmed = token.trim();
        const isWord = /\b[a-zA-Z]+(?:'[a-zA-Z]+)?\b/.test(trimmed);

        if (!isWord) {
          return <React.Fragment key={`token-${index}`}>{token}</React.Fragment>;
        }

        return (
          <span
            key={`token-${index}`}
            className="cursor-pointer rounded-lg px-1 py-0.5 text-foreground transition hover:bg-primary/10 hover:text-primary"
            onClick={(event) => {
              event.stopPropagation();
              handleWordClick(trimmed);
            }}
          >
            {token}
          </span>
        );
      });
    }

    // Create regex to match the term (supports phrases)
    const escapedTerm = termLower.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const termRegex = new RegExp(`\\b(${escapedTerm})\\b`, 'gi');
    
    // Split text while preserving the matched term
    const parts = item.english.split(termRegex);
    
    return parts.map((part, index) => {
      if (!part) return null;
      
      const isTermMatch = termRegex.test(part);
      termRegex.lastIndex = 0; // Reset regex for next test
      
      if (isTermMatch) {
        // This is the highlighted term/phrase
        return (
          <span
            key={`term-${index}`}
            className={`cursor-pointer rounded-lg px-1 py-0.5 font-semibold ${termHighlightClass} text-background shadow-sm transition`}
            onClick={(event) => {
              event.stopPropagation();
              handleWordClick(part.trim());
            }}
          >
            {part}
          </span>
        );
      } else {
        // Split non-term parts into clickable words
        const tokens = part.split(/(\s+|[^\w\s])/);
        return tokens.map((token, tokenIndex) => {
          const trimmed = token.trim();
          const isWord = /\b[a-zA-Z]+(?:'[a-zA-Z]+)?\b/.test(trimmed);

          if (!isWord) {
            return <React.Fragment key={`token-${index}-${tokenIndex}`}>{token}</React.Fragment>;
          }

          return (
            <span
              key={`token-${index}-${tokenIndex}`}
              className="cursor-pointer rounded-lg px-1 py-0.5 text-foreground transition hover:bg-primary/10 hover:text-primary"
              onClick={(event) => {
                event.stopPropagation();
                handleWordClick(trimmed);
              }}
            >
              {token}
            </span>
          );
        });
      }
    }).filter(Boolean);
  }, [item.english, item.term, colorIndex, handleWordClick]);

  const hasTtsAction = Boolean(item.english && !currentAudioUrl);
  const jumpTimestamp = typeof item.timestamp === "number" ? item.timestamp : undefined;
  const hasJumpAction = jumpTimestamp !== undefined && Boolean(onTimestampClick);
  const showActionRow = hasTtsAction || hasJumpAction || Boolean(currentAudioUrl);

  return (
    <Card
      className={`h-full rounded-3xl border transition hover:shadow-lg ${
        isCompleted
          ? 'bg-green-400 shadow-md dark:bg-emerald-900/30 dark:border-emerald-900/40'
          : 'border-border/60 bg-background/95 shadow-sm'
      }`}
    >
      <CardContent className="space-y-4 p-5">
        <div className="space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className="rounded-full px-3 py-0.5 text-[10px] bg-background/95 uppercase tracking-wide"
              >
                #{item.sequence ?? "?"}
              </Badge>
              {item.term && (
                <span className="inline-flex items-center rounded-full bg-muted/40 px-2 py-0.5 text-[11px] font-medium capitalize text-muted-foreground">
                  {item.term}
                </span>
              )}
              {jumpTimestamp !== undefined && (
                <span className="flex items-center gap-1 text-xs uppercase tracking-wide text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" />
                  {formatTimestamp(jumpTimestamp)}
                  {durationLabel && <span className="text-muted-foreground/60">• {durationLabel}</span>}
                </span>
              )}
            </div>
            <QuizCompletionIndicator completed={isCompleted} />
          </div>

          {isCompleted && (
            <div className="flex items-center gap-2 rounded-2xl bg-yellow-300/80 px-3 py-2 text-xs font-semibold text-red-500 shadow-sm ring-1 ring-emerald-200/70 dark:bg-emerald-900/40 dark:text-emerald-100 dark:ring-emerald-900/40">
              <CheckCircle2 className="h-3.5 w-3.5" /> {t('quizSection.messages.completed', 'Great job! This term is fully shadowed.')}
            </div>
          )}

          {item.english && (
            <p className="text-lg font-semibold text-foreground">
              {interactiveSentence}
            </p>
          )}

          {item.explanation && (
            <p className="text-sm text-muted-foreground/80">{extractStringValue(item.explanation)}</p>
          )}
        </div>

        {showActionRow && (
          <div className="flex flex-wrap items-center justify-start gap-2 pt-2">
            {currentAudioUrl && (
              <div className="flex-1 min-w-[220px]">
                <AudioWave
                  url={currentAudioUrl}
                  dark={false}
                  autoPlay={false}
                  className={isCompleted ? "bg-indigo-50" : undefined}
                />
              </div>
            )}
            {hasTtsAction && (
              <Button
                onClick={handleGenerateAudio}
                variant="outline"
                size="icon"
                className="h-9 w-9 rounded-full border-dashed"
                disabled={ttsLoading}
              >
                {ttsLoading ? (
                  <svg
                    className="h-5 w-5 animate-spin text-foreground"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 2v4" />
                    <path d="m16.2 7.8 2.9-2.9" />
                    <path d="M18 12h4" />
                    <path d="m16.2 16.2 2.9 2.9" />
                    <path d="M12 18v4" />
                    <path d="m4.9 19.1 2.9-2.9" />
                    <path d="M2 12h4" />
                    <path d="m4.9 4.9 2.9 2.9" />
                  </svg>
                ) : (
                  <IconPlayerPlay size={22} fill="currentColor" />
                )}
              </Button>
            )}
            {hasJumpAction && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 border rounded-full text-muted-foreground/80 hover:text-primary"
                onClick={handleJump}
                title={jumpTimestamp !== undefined ? formatTimestamp(jumpTimestamp) : undefined}
              >
                <ArrowUpRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}

        {ttsError && <p className="text-xs text-destructive">{ttsError}</p>}

        {item.chinese && (
          <TranslationToggle
            showTranslation={showTranslation}
            onToggle={() => setShowTranslation((prev) => !prev)}
            translation={item.chinese}
          />
        )}

        <QuizShadowButton
          item={item}
          episodeId={episodeId}
          episodeTitle={episodeTitle}
          episodeThumbnail={episodeThumbnail}
          audioUrl={currentAudioUrl ?? undefined}
          onProgressUpdate={onProgressUpdate}
        />

        <div className="sr-only">
          <SpeakerIcon divRef={speakRef} lang="en" text={item.english || item.term || ""} />
        </div>
      </CardContent>
    </Card>
  );
}

function QuizCompletionIndicator({ completed }: { completed?: boolean }) {
  const { user } = useUser();
  const { t } = useI18n();

  if (!user?.id) return null;

  if (completed) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100/85 px-2.5 py-1 text-xs font-semibold text-emerald-700 shadow-sm ring-1 ring-emerald-200/80 dark:bg-emerald-900/50 dark:text-emerald-100 dark:ring-emerald-900/50">
        <CheckCircle2 className="h-3.5 w-3.5" />
        {t('quizSection.status.completed', 'Completed')}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-dashed border-muted-foreground/50 px-2.5 py-1 text-xs font-medium text-muted-foreground">
      {t('quizSection.status.todo', 'Todo')}
    </span>
  );
}

function TranslationToggle({
  showTranslation,
  onToggle,
  translation,
}: {
  showTranslation: boolean;
  onToggle: () => void;
  translation: string;
}) {
  const { t } = useI18n();
  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-2 text-xs font-semibold text-muted-foreground/70 transition hover:text-primary"
      >
        <span>{showTranslation
          ? t('quizSection.translation.hide', 'Hide translation')
          : t('quizSection.translation.show', 'Show translation')}
        </span>
      </button>
      {showTranslation && (
        <div className="rounded-2xl bg-muted/30 px-4 py-3 text-sm text-muted-foreground/90">
          {translation}
        </div>
      )}
    </div>
  );
}

function OverviewRow({
  label,
  value,
  icon,
  highlight = false
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {icon && <div className="text-muted-foreground/60">{icon}</div>}
        <span className="text-xs uppercase tracking-wide text-muted-foreground/70">{label}</span>
      </div>
      <span className={`text-sm font-medium ${
        highlight
          ? "text-primary font-bold text-base bg-primary/10 px-2 py-1 rounded-lg"
          : "text-foreground"
      }`}>
        {value}
      </span>
    </div>
  );
}

function formatTimestamp(timestamp: number) {
  const minutes = Math.floor(timestamp / 60);
  const seconds = Math.floor(timestamp % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function formatDuration(durationInSeconds?: number) {
  if (!durationInSeconds || durationInSeconds <= 0) {
    return "";
  }

  const minutes = Math.floor(durationInSeconds / 60);
  const seconds = Math.round(durationInSeconds % 60);

  if (minutes <= 0) {
    return `${seconds}s`;
  }

  if (seconds === 0) {
    return `${minutes}m`;
  }

  return `${minutes}m ${seconds}s`;
}

function OverviewSkeleton() {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-3 w-16" />
        </div>
        <Skeleton className="h-4 w-12" />
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-3 w-16" />
        </div>
        <Skeleton className="h-4 w-20" />
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-3 w-16" />
        </div>
        <Skeleton className="h-6 w-14 rounded-lg bg-primary/10" />
      </div>
    </div>
  );
}

function QuizItemCardSkeleton({ colorIndex }: { colorIndex?: number }) {
  return (
    <Card className="rounded-3xl border border-border/60 bg-background/95 shadow-sm">
      <CardContent className="space-y-4 p-5">
        <div className="space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <Skeleton className="h-6 w-8 rounded-full" />
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-4 w-20" />
            </div>
            <Skeleton className="h-6 w-12 rounded-full" />
          </div>

          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-3/4" />

          <Skeleton className="h-4 w-2/3" />
        </div>

        <div className="flex items-center gap-1">
          <Skeleton className="h-8 w-8 rounded" />
        </div>

        <Skeleton className="h-12 w-full rounded-xl" />

        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-20 rounded-xl" />
        </div>
      </CardContent>
    </Card>
  );
}

function QuizSectionSkeleton() {
  return (
    <div className="grid gap-2 sm:gap-6 lg:grid-cols-[260px_1fr]">
      <Card className="order-2 h-fit rounded-3xl border bg-card lg:order-1">
        <CardContent className="space-y-4 p-5">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-10 w-24" />
          <div className="space-y-3">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-5 w-2/3" />
          </div>
          <Skeleton className="h-9 w-full" />
        </CardContent>
      </Card>
      <div className="order-1 space-y-4 lg:order-2">
        {[0, 1, 2].map((index) => (
          <Card key={index} className="rounded-3xl border bg-card">
            <CardContent className="space-y-3 p-5">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-6 w-1/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-5 w-2/3" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
function QuizShadowButton({
  item,
  episodeId,
  episodeTitle,
  episodeThumbnail,
  audioUrl,
  onProgressUpdate,
}: {
  item: QuizItem;
  episodeId?: string;
  episodeTitle?: string;
  episodeThumbnail?: string;
  audioUrl?: string;
  onProgressUpdate?: () => void;
}) {
  const { user } = useUser();
  const { saveRecord, saving } = useSaveShadowingRecord();
  const { t } = useI18n();
  const {
    isRecording,
    startRecording,
    stopRecording,
    audioUrl: recordedAudioUrl,
    transcribedText,
    isTranscribing,
    transcriptionError,
  } = useAudioRecorder();
  const [open, setOpen] = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [attempt, setAttempt] = useState(0);
  const startTimeRef = useRef<number | null>(null);
  const lastTranscriptRef = useRef<string | null>(null);

  const handleDialogWordClick = useCallback(async (word: string) => {
    const trimmed = word.trim();
    if (!trimmed) return;

    try {
      const { doSpeak } = await import("@/components/tts");
      const controller = new AbortController();

      await doSpeak({
        provider: "EdgeTTS",
        text: trimmed,
        lang: "en",
        rate: 10,
        volume: 100,
        signal: controller.signal,
      });
    } catch (error) {
      console.error("Failed to speak dialog word", error);
    }
  }, []);

  const interactiveDialogSentence = useMemo<React.ReactNode[]>(() => {
    if (!item.english) return [];

    return item.english.split(/(\s+|[^\w\s])/).map((token, index) => {
      const trimmed = token.trim();
      const isWord = /\b[a-zA-Z]+(?:'[a-zA-Z]+)?\b/.test(trimmed);

      if (!isWord) {
        return <React.Fragment key={`dialog-token-${index}`}>{token}</React.Fragment>;
      }

      return (
        <span
          key={`dialog-token-${index}`}
          className="cursor-pointer rounded-lg px-1 py-0.5 text-foreground transition hover:bg-primary/10 hover:text-primary"
          onClick={(event) => {
            event.stopPropagation();
            handleDialogWordClick(trimmed);
          }}
        >
          {token}
        </span>
      );
    });
  }, [item.english, handleDialogWordClick]);

  useEffect(() => {
    if (!transcribedText || !item.english) return;
    if (lastTranscriptRef.current === transcribedText) return;
    lastTranscriptRef.current = transcribedText;

    const computedAccuracy = calculateOptimalSimilarity(item.english, transcribedText);
    setAccuracy(computedAccuracy);

    const duration = startTimeRef.current
      ? Math.max(1, Math.round((Date.now() - startTimeRef.current) / 1000))
      : 0;

    // Persist the record only if user is logged in
    const persist = async () => {
      if (!user?.id) return;
      await saveRecord({
        userId: user.id,
        episodeId: `${episodeId || "episode"}:quiz`,
        episodeTitle: episodeTitle || t('quizSection.shadowing.defaultTitle', 'Episode Quiz Practice'),
        episodeThumbnail,
        sentenceText: item.english || "",
        userTranscription: transcribedText,
        accuracy: computedAccuracy,
        duration,
        quizSequence: item.sequence, // 添加 quiz sequence 信息
      });
    };

    persist().catch((error) => {
      console.error("Failed to save shadowing record", error);
    });
  }, [transcribedText, item.english, episodeId, episodeTitle, episodeThumbnail, saveRecord, t, user?.id]);

  const handleToggleRecording = async () => {
    if (isRecording) {
      stopRecording();
      return;
    }

    try {
      startTimeRef.current = Date.now();
      lastTranscriptRef.current = null;
      setAccuracy(null);
      setAttempt((prev) => prev + 1);
      await startRecording();
    } catch (error) {
      console.error("Failed to start recording", error);
    }
  };

  const disabled = !item.english;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 px-2 rounded-xl"
        onClick={() => setOpen(true)}
        disabled={disabled}
      >
        <Speech className="mr-1 h-4 w-4" /> {t('quizSection.shadowing.cta', 'Shadowing')}
      </Button>
      <Dialog open={open} onOpenChange={(next) => {
        if (!next && isRecording) {
          stopRecording();
        }
        setOpen(next);
      }}>
        <DialogContent className="sm:max-w-lg rounded-3xl border-border">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">
              {t('quizSection.shadowing.dialogTitle', 'Shadowing Practice')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm text-muted-foreground">
                {t('quizSection.shadowing.instructions', 'Listen and repeat the sentence')}
              </div>
              {item.chinese && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-xs"
                  onClick={() => setShowTranslation((prev) => !prev)}
                >
                  <Languages className="mr-1 h-4 w-4" />
                  {showTranslation
                    ? t('quizSection.shadowing.hideTranslation', 'Hide translation')
                    : t('quizSection.shadowing.showTranslation', 'Show translation')}
                </Button>
              )}
            </div>

            <div className="rounded-2xl bg-muted/30 px-4 py-3 text-sm text-foreground/90 leading-relaxed">
              {interactiveDialogSentence}
            </div>

            {showTranslation && item.chinese && (
              <div className="rounded-2xl bg-muted/20 px-4 py-3 text-sm text-muted-foreground/90">
                {item.chinese}
              </div>
            )}

            {audioUrl ? (
              <AudioWave url={audioUrl} dark={false} autoPlay={false} />
            ) : (
              <p className="text-xs text-muted-foreground">
                {t('quizSection.shadowing.audioUnavailable', 'Reference audio not available yet. Generate it from the quiz card to listen.')}
              </p>
            )}

            {/* Dictation-style circular record button */}
            <div className="flex items-center gap-3">
              <div className="relative">
                <button
                  type="button"
                  onClick={handleToggleRecording}
                  disabled={disabled || isTranscribing}
                  aria-label={isRecording
                    ? t('quizSection.shadowing.stopRecording', 'Stop Recording')
                    : t('quizSection.shadowing.startShadowing', 'Start Shadowing')}
                  aria-pressed={isRecording}
                  aria-busy={isTranscribing}
                  className={`
                    w-9 h-9 border p-1.5 rounded-full cursor-pointer
                    inline-flex items-center justify-center
                    focus:bg-black/[0.15] dark:focus:bg-white/[0.7]
                    focus:outline-none focus-visible:outline-2 focus-visible:outline-black/80 dark:focus-visible:outline-white/80 focus-visible:outline-offset-2
                    ${isTranscribing ? 'opacity-50 cursor-not-allowed' : ''}
                    ${transcriptionError ? 'bg-red-100 hover:bg-red-200' : ''}
                    ${disabled ? 'opacity-30 cursor-not-allowed' : ''}
                  `}
                  style={{
                    boxSizing: 'border-box',
                    transition: isRecording ? 'background-color 0.2s ease' : 'box-shadow 0.05s linear, background-color 0.2s ease',
                    boxShadow: isRecording
                      ? '0 0 0 2px rgba(0, 0, 0, 0.2)'
                      : '0 0 0 0px rgba(0, 0, 0, 0)'
                  }}
                >
                  {/* Idle state - Microphone icon */}
                  {!isRecording && !isTranscribing && !transcriptionError && (
                    <svg
                      className="w-full h-full text-black/80 dark:text-black"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 18.75a6 6 0 0 0 6-6v-1.5m-6 7.5a6 6 0 0 1-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 0 1-3-3V4.5a3 3 0 1 1 6 0v8.25a3 3 0 0 1-3 3Z"
                      />
                    </svg>
                  )}

                  {/* Recording state - Filled circle */}
                  {isRecording && (
                    <svg
                      className="w-full h-full text-red-500"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                  )}

                  {/* Processing state - Rotating spinner */}
                  {isTranscribing && (
                    <svg
                      className="w-full h-full text-black/80 dark:text-black animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M12 2v4" />
                      <path d="m16.2 7.8 2.9-2.9" />
                      <path d="M18 12h4" />
                      <path d="m16.2 16.2 2.9 2.9" />
                      <path d="M12 18v4" />
                      <path d="m4.9 19.1 2.9-2.9" />
                      <path d="M2 12h4" />
                      <path d="m4.9 4.9 2.9 2.9" />
                    </svg>
                  )}

                  {/* Error state - Exclamation */}
                  {transcriptionError && !isRecording && !isTranscribing && (
                    <svg
                      className="w-full h-full text-red-600"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <line x1="12" x2="12" y1="4" y2="14" />
                      <line x1="12" x2="12.01" y1="20" y2="20" />
                    </svg>
                  )}
                </button>
              </div>
              {saving && <SpinnerIcon size="sm" color="hsl(var(--muted-foreground))" />}
            </div>

            {recordedAudioUrl && (
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                  {t('quizSection.shadowing.yourRecording', 'Your recording')}
                </p>
                <AudioWave url={recordedAudioUrl} dark={false} autoPlay={false} />
              </div>
            )}

            {transcriptionError && (
              <p className="text-xs text-destructive">{transcriptionError}</p>
            )}

            {transcribedText && item.english && (
              <div className="space-y-3">
                <TextDiffHighlight
                  originalText={item.english}
                  userTranscription={transcribedText}
                  accuracy={accuracy || 0}
                />
                <div className="flex items-center justify-between text-xs text-muted-foreground px-2">
                  <span>
                    {t('quizSection.shadowing.attemptsLabel', 'Attempts')}: {attempt}
                  </span>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
